import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { GoogleLinkCard } from "../components/GoogleLinkCard";
import { type GoogleIntegrationView } from "../lib/googleIntegration";
import { getGoogleConsentStatus } from "../lib/googleConsentStatus";
import {
  GOOGLE_SCOPE_BUNDLES,
  createDefaultGoogleScopeGrantedMap,
  getGoogleIntegrationAggregateStatusLabel,
  getGoogleScopeBundleLabel,
  isGoogleLinkMode,
  isGoogleScopeBundle,
  resolveGoogleIntegrationAggregateState,
  type GoogleLinkMode,
  type GoogleScopeBundle,
  type GoogleScopeGrantedMap,
} from "../lib/googleScopes";
import { UI_TEXT_TONE } from "../lib/uiTone";

type GoogleLinkRouteState = {
  returnTo?: string;
  returnState?: unknown;
  scopeBundle?: GoogleScopeBundle;
  mode?: GoogleLinkMode;
};

const GOOGLE_OAUTH_POPUP_NAME = "wizmate-google-oauth";
const GOOGLE_OAUTH_POPUP_FEATURES = "popup=yes,width=520,height=720";

export default function GoogleLinkPopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as GoogleLinkRouteState;

  const routeScopeBundle = routeState.scopeBundle;
  const queryScopeBundle = searchParams.get("scopeBundle");
  const parsedScopeBundle = isGoogleScopeBundle(routeScopeBundle)
    ? routeScopeBundle
    : isGoogleScopeBundle(queryScopeBundle)
      ? queryScopeBundle
      : GOOGLE_SCOPE_BUNDLES.bundle;

  const routeMode = routeState.mode;
  const queryMode = searchParams.get("mode");
  const parsedMode = isGoogleLinkMode(routeMode)
    ? routeMode
    : isGoogleLinkMode(queryMode)
      ? queryMode
      : "single";

  const returnTo = routeState.returnTo || searchParams.get("returnTo") || "/home";

  const [activeScopeBundle, setActiveScopeBundle] = useState<GoogleScopeBundle>(parsedScopeBundle);
  const [scopeGrantedMap, setScopeGrantedMap] = useState<GoogleScopeGrantedMap>(
    createDefaultGoogleScopeGrantedMap()
  );
  const [bundleGranted, setBundleGranted] = useState(false);
  const [scopeStatusLoaded, setScopeStatusLoaded] = useState(false);
  const [allConsentMessage, setAllConsentMessage] = useState<string | null>(null);
  const [allConsentError, setAllConsentError] = useState<string | null>(null);
  const [autoStartToken, setAutoStartToken] = useState<number | undefined>(undefined);
  const preparedPopupRef = useRef<Window | null>(null);

  const closePopup = () => {
    navigate(returnTo, { replace: true, state: routeState.returnState });
  };

  const aggregateStatus = useMemo(
    () => resolveGoogleIntegrationAggregateState(scopeGrantedMap),
    [scopeGrantedMap]
  );
  const aggregateLabel = getGoogleIntegrationAggregateStatusLabel(aggregateStatus);
  const isSelectorMode = parsedMode === "selector" || parsedMode === "all";
  const anyIndividualGranted =
    scopeGrantedMap[GOOGLE_SCOPE_BUNDLES.receipt] || scopeGrantedMap[GOOGLE_SCOPE_BUNDLES.news];
  const bundleConsentDisabled =
    !scopeStatusLoaded || bundleGranted || anyIndividualGranted;
  const shouldForceConnectedStatusForCard =
    activeScopeBundle === GOOGLE_SCOPE_BUNDLES.bundle &&
    isSelectorMode &&
    (bundleGranted || anyIndividualGranted || aggregateStatus === "full");
  const shouldForceShowDisconnectButtonForCard =
    activeScopeBundle === GOOGLE_SCOPE_BUNDLES.bundle &&
    isSelectorMode &&
    (bundleGranted || anyIndividualGranted || aggregateStatus === "full");

  const refreshScopeGrantedMap = useCallback(async () => {
    try {
      const consentStatus = await getGoogleConsentStatus();
      const nextMap = createDefaultGoogleScopeGrantedMap();
      nextMap[GOOGLE_SCOPE_BUNDLES.receipt] = consentStatus.receipt.granted;
      nextMap[GOOGLE_SCOPE_BUNDLES.news] = consentStatus.news.granted;
      const aggregateGranted =
        consentStatus.bundle.granted ||
        (nextMap[GOOGLE_SCOPE_BUNDLES.receipt] && nextMap[GOOGLE_SCOPE_BUNDLES.news]);

      setScopeGrantedMap(nextMap);
      setBundleGranted(aggregateGranted);
      return nextMap;
    } catch {
      const fallbackMap = createDefaultGoogleScopeGrantedMap();
      setScopeGrantedMap(fallbackMap);
      setBundleGranted(false);
      return fallbackMap;
    } finally {
      setScopeStatusLoaded(true);
    }
  }, []);

  const triggerAutoStart = useCallback(() => {
    setAutoStartToken((prev) => (typeof prev === "number" ? prev + 1 : 1));
  }, []);

  const preopenGoogleOAuthPopup = useCallback(() => {
    const popup = window.open("", GOOGLE_OAUTH_POPUP_NAME, GOOGLE_OAUTH_POPUP_FEATURES);
    preparedPopupRef.current = popup;
  }, []);

  const consumePreparedPopup = useCallback(() => {
    const popup = preparedPopupRef.current;
    preparedPopupRef.current = null;
    return popup;
  }, []);

  const startBundleConsent = useCallback(async () => {
    preopenGoogleOAuthPopup();
    setAllConsentMessage(null);
    setAllConsentError(null);

    const latestMap = await refreshScopeGrantedMap();
    if (latestMap[GOOGLE_SCOPE_BUNDLES.receipt] || latestMap[GOOGLE_SCOPE_BUNDLES.news]) {
      setAllConsentError(
        "個別連携（領収書・ニュース）がすでに設定されています。まとめて連携との同時使用はサポートされていません。"
      );
      return;
    }

    setActiveScopeBundle(GOOGLE_SCOPE_BUNDLES.bundle);
    triggerAutoStart();
  }, [preopenGoogleOAuthPopup, refreshScopeGrantedMap, triggerAutoStart]);

  const handleScopeStatusChange = useCallback(
    (scopeBundle: GoogleScopeBundle, status: GoogleIntegrationView) => {
      setScopeGrantedMap((prev) => ({
        ...prev,
        [scopeBundle]: status.state === "connected",
      }));
    },
    []
  );

  const handleScopeConnected = useCallback(
    async (scopeBundle: GoogleScopeBundle) => {
      if (scopeBundle === GOOGLE_SCOPE_BUNDLES.bundle) {
        setBundleGranted(true);
        setAllConsentMessage("まとめて連携が完了しました。");
      } else {
        setScopeGrantedMap((prev) => ({
          ...prev,
          [scopeBundle]: true,
        }));
      }
      void refreshScopeGrantedMap();
    },
    [refreshScopeGrantedMap]
  );

  const handleScopeInterrupted = useCallback(
    (scopeBundle: GoogleScopeBundle) => {
      setAllConsentError(
        `「${getGoogleScopeBundleLabel(scopeBundle)}」の連携が中断されました。状態を確認して再試行してください。`
      );
      void refreshScopeGrantedMap();
    },
    [refreshScopeGrantedMap]
  );

  const handleScopeDisconnected = useCallback(() => {
    setBundleGranted(false);
    setScopeGrantedMap(createDefaultGoogleScopeGrantedMap());
  }, []);

  useEffect(() => {
    void refreshScopeGrantedMap();
  }, [refreshScopeGrantedMap]);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-xl items-center justify-center">
        <div className="relative w-full max-w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-4 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <button
            type="button"
            onClick={closePopup}
            aria-label="閉じる"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EADBC8] bg-[#FFF8F4] text-[#7C5A4E] transition hover:bg-white"
          >
            ×
          </button>

          <div className="mb-5 pr-12">
            <h1 className={`text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>Google連携</h1>
            <p className={`mt-2 text-sm leading-6 ${UI_TEXT_TONE.body}`}>
              Google権限を連携して、ニュース配信と領収書保存が使えるようになります。
            </p>
          </div>

          {isSelectorMode ? (
            <div className="mb-5 rounded-[1.6rem] border border-[#EADBC8] bg-[#FFF8F4] px-4 py-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-black text-[#7C5A4E]">連携ステータス</p>
                <span className="rounded-full border border-[#E6DACD] bg-white px-3 py-1 text-xs font-black text-[#A46D4C]">
                  {aggregateLabel}
                </span>
              </div>

              <div className="mt-4 space-y-2 text-sm">
                {/* TODO: 個別連携(領収書・ニュース)の同時使用バグ修正後に再表示する */}
                {/* <div className="flex items-center justify-between gap-3 rounded-xl border border-[#F1E3D8] bg-white px-3 py-2">
                  <span className="font-bold text-[#5D4D46]">
                    {getGoogleScopeBundleLabel(GOOGLE_SCOPE_BUNDLES.receipt)}
                  </span>
                  <span className="text-xs font-bold text-[#7B6861]">
                    {scopeGrantedMap[GOOGLE_SCOPE_BUNDLES.receipt] ? "連携済み" : "未連携"}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-[#F1E3D8] bg-white px-3 py-2">
                  <span className="font-bold text-[#5D4D46]">
                    {getGoogleScopeBundleLabel(GOOGLE_SCOPE_BUNDLES.news)}
                  </span>
                  <span className="text-xs font-bold text-[#7B6861]">
                    {scopeGrantedMap[GOOGLE_SCOPE_BUNDLES.news] ? "連携済み" : "未連携"}
                  </span>
                </div> */}
                <div className="flex items-center justify-between gap-3 rounded-xl border border-[#F1E3D8] bg-white px-3 py-2">
                  <span className="font-bold text-[#5D4D46]">
                    {getGoogleScopeBundleLabel(GOOGLE_SCOPE_BUNDLES.bundle)}
                  </span>
                  <span className="text-xs font-bold text-[#7B6861]">
                    {bundleGranted ? "連携済み" : "未連携"}
                  </span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2">
                {/* TODO: 個別連携(領収書・ニュース)の同時使用バグ修正後に再表示する */}
                {/* <button
                  type="button"
                  data-testid="google-consent-receipt-button"
                  onClick={() => openSingleScopeConsent(GOOGLE_SCOPE_BUNDLES.receipt)}
                  disabled={receiptConsentDisabled}
                  className="rounded-full border border-[#E4D5C8] bg-white px-3 py-2 text-xs font-black text-[#6A4F44] transition hover:bg-[#FFFDFB] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  領収書を連携
                </button>
                <button
                  type="button"
                  data-testid="google-consent-news-button"
                  onClick={() => openSingleScopeConsent(GOOGLE_SCOPE_BUNDLES.news)}
                  disabled={newsConsentDisabled}
                  className="rounded-full border border-[#E4D5C8] bg-white px-3 py-2 text-xs font-black text-[#6A4F44] transition hover:bg-[#FFFDFB] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  ニュースを連携
                </button> */}
                <button
                  type="button"
                  data-testid="google-consent-bundle-button"
                  onClick={() => void startBundleConsent()}
                  disabled={bundleConsentDisabled}
                  className="rounded-full bg-[#B07A55] px-3 py-2 text-xs font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  まとめて連携
                </button>
              </div>

              {allConsentMessage ? (
                <p className="mt-3 rounded-xl border border-[#D8E7D3] bg-[#F7FBF3] px-3 py-2 text-xs font-bold text-[#58705A]">
                  {allConsentMessage}
                </p>
              ) : null}
              {allConsentError ? (
                <p className="mt-3 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-3 py-2 text-xs font-bold text-[#A1493E]">
                  {allConsentError}
                </p>
              ) : null}
            </div>
          ) : null}

          <GoogleLinkCard
            scopeBundle={activeScopeBundle}
            autoStartToken={autoStartToken}
            showServiceSummary={!isSelectorMode}
            showConnectButton={!isSelectorMode}
            forceConnectedStatus={shouldForceConnectedStatusForCard}
            forceShowDisconnectButton={shouldForceShowDisconnectButtonForCard}
            consumePreparedPopup={consumePreparedPopup}
            onStatusChange={handleScopeStatusChange}
            onConnected={handleScopeConnected}
            onDisconnected={handleScopeDisconnected}
            onInterrupted={handleScopeInterrupted}
          />
        </div>
      </div>
    </main>
  );
}
